import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generateClient } from '@aws-amplify/api';
import { listEventsLookup } from '../constants/customQueries';
import { setEventsLookup } from '../store/ducks/event';
import { setPageEvent } from '../store/ducks/pageEvents';
import {
  EventLookupItem,
  persistPageEventId,
  pickDefaultEvent,
  readCachedEventsLookup,
  writeCachedEventsLookup,
} from '../helpers/selectedEvent';

const client = generateClient();

/** Lookup older than this is refreshed in the background on next use. */
export const EVENTS_LOOKUP_TTL_MS = 5 * 60 * 1000;

// One request app-wide: concurrent callers share the in-flight promise.
let inFlight: Promise<EventLookupItem[] | null> | null = null;
let forceNextLoad = false;

/** Call after creating / updating / deleting an event. */
export const markEventsLookupStale = () => {
  forceNextLoad = true;
};

/**
 * Event each page got automatically (default rule), per page key. Such a
 * selection follows the default when the events list changes; an explicit
 * choice (setEventId) removes the page from here and is kept as is.
 */
export const autoPickedByPage = new Map<string, string>();

const fetchEventsLookup = async (): Promise<EventLookupItem[]> => {
  let items: EventLookupItem[] = [];
  let nextToken: string | null | undefined = null;
  do {
    const res: any = await client.graphql({
      query: listEventsLookup,
      variables: { filter: { deleted: { eq: '0' } }, limit: 1000, nextToken },
      authMode: 'userPool',
    });
    const page = res?.data?.listEvents;
    items = items.concat((page?.items ?? []).filter(Boolean));
    nextToken = page?.nextToken;
  } while (nextToken);
  return items;
};

/**
 * Load the lean events list into Redux (`events.lookup`) unless it is already
 * there and fresh. Safe to call from many components at once.
 */
export const ensureEventsLookup = (
  dispatch: (action: any) => any,
  lookupLoadedAt: number,
  options: { force?: boolean } = {},
): Promise<EventLookupItem[] | null> => {
  const fresh =
    lookupLoadedAt > 0 && Date.now() - lookupLoadedAt < EVENTS_LOOKUP_TTL_MS;
  if (fresh && !options.force && !forceNextLoad) return Promise.resolve(null);
  if (inFlight) return inFlight;
  forceNextLoad = false;
  inFlight = fetchEventsLookup()
    .then((items) => {
      dispatch(setEventsLookup(items));
      writeCachedEventsLookup(items);
      return items;
    })
    .catch((err) => {
      console.error('Failed to load events lookup', err);
      // Mark as loaded on first failure so pages don't wait forever (keeps
      // the list saved from the last session, if any).
      if (!lookupLoadedAt) dispatch(setEventsLookup(readCachedEventsLookup()));
      return null;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

/**
 * App-wide lean events list (stale-while-revalidate, 5 min TTL; starts from
 * the copy saved in localStorage after a reload).
 * `loaded`: a list is available (fresh or saved); `fresh`: loaded from the
 * API in this session.
 */
export const useEventsLookup = () => {
  const dispatch = useDispatch();
  const events: EventLookupItem[] = useSelector(
    (state: any) => state.events.lookup,
  );
  const lookupLoadedAt: number = useSelector(
    (state: any) => state.events.lookupLoadedAt,
  );

  useEffect(() => {
    ensureEventsLookup(dispatch, lookupLoadedAt);
    // Only on mount: TTL is re-checked by the next consumer that mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = useCallback(
    () => ensureEventsLookup(dispatch, lookupLoadedAt, { force: true }),
    [dispatch, lookupLoadedAt],
  );

  const list = events || [];
  return {
    events: list,
    loaded: lookupLoadedAt > 0 || list.length > 0,
    fresh: lookupLoadedAt > 0,
    reload,
  };
};

/**
 * Selected event of the page that is open (per page, see
 * helpers/selectedEvent.ts and components/Dashboard/PageEventScope.tsx).
 * `eventId` is `generalFilters.event`, which <PageEventScope> sets to this
 * page's own selection (saved choice or default) before the page renders.
 * `setEventId` changes ONLY this page's selection and persists it to
 * localStorage (`admin.selectedEvent.<pageKey>`).
 */
export const useGlobalEvent = () => {
  const dispatch = useDispatch();
  const eventId: string = useSelector(
    (state: any) => state.generalFilters.event || '',
  );
  const pageKey: string = useSelector(
    (state: any) => state.pageEvents?.activePage || '',
  );
  const { events, loaded, reload } = useEventsLookup();

  const selectedEvent = useMemo(
    () => (eventId ? events.find((e) => e.id === eventId) ?? null : null),
    [events, eventId],
  );

  const setEventId = useCallback(
    (id: string) => {
      const next = id || '';
      // Explicit choice: remembered for this page only.
      autoPickedByPage.delete(pageKey);
      persistPageEventId(pageKey, next);
      if (next === eventId) return;
      dispatch(setPageEvent(pageKey, next, true));
    },
    [dispatch, eventId, pageKey],
  );

  return {
    eventId,
    setEventId,
    selectedEvent,
    eventName: selectedEvent?.name ?? '',
    events,
    loaded,
    reload,
    /**
     * True once the page can query with its event: an event is selected, or
     * the list is known and there is no event to select.
     */
    ready: !!eventId || (loaded && !pickDefaultEvent(events)),
  };
};

export default useGlobalEvent;
